import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { IdentityStatusEnum } from "@/lib/enum/identity-status.enum";
import { deepCopy } from "@/lib/utility/deep-copy";
import { UserEntity } from "@/models/userEntity";
import { default as authService } from "@/services/mrfiktiv/services/authService";
import AdminUserService from "@/services/mrfiktiv/services/adminUserService";
import {
  MrfiktivCreateUserDtoGen,
  MrfiktivIdentityInformationViewModelGen,
  MrfiktivPermissionDtoGen,
  MrfiktivUpdateUserDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import AdminSharedUserService from "@/services/shared/adminSharedUserService";
import { ThgIdentityInformationViewModelGen, ThgUserViewmodelGen } from "@/services/thg/v1/data-contracts";
import { AuthRolesEnum } from "@/store/enum/authRolesEnum";
import store from "@/store/VuexPlugin";
import Vue from "vue";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { IUserPermissionUpdateDto } from "../interface/user.permission.dto.interface";
import { IUserRoleUpdateDto } from "../interface/user.role.dto.interface";
import { ErrorLogModule } from "./error-log";

@Module({
  dynamic: true,
  namespaced: true,
  name: "userAdministrationStore",
  store
})
class UserAdministrationStore extends VuexModule {
  users: UserEntity[] = [];
  selectedUser: UserEntity = {} as any;

  _emptyUserEntity: UserEntity = {
    _id: "",
    id: "",
    userName: "",
    firstName: "",
    lastName: "",
    contact: {
      email: "",
      phone: ""
    },
    address: {},
    roles: [],
    authInfo: "" as any,
    countryCode: CountryCodeEnum.germany,
    agbsAccepted: false
  };

  private _selectedUserIdentity: ThgIdentityInformationViewModelGen | MrfiktivIdentityInformationViewModelGen = {
    status: IdentityStatusEnum.UNKNOWN
  };

  get selectedUserIdentity(): ThgIdentityInformationViewModelGen | MrfiktivIdentityInformationViewModelGen {
    return this._selectedUserIdentity;
  }

  @Mutation
  _mutateSelectedUserIdentity(identity: ThgIdentityInformationViewModelGen | MrfiktivIdentityInformationViewModelGen) {
    this._selectedUserIdentity = identity;
  }

  @Mutation
  setSelectedUser(user: UserEntity) {
    this.selectedUser = user;
  }

  @Mutation
  addUserToUsers(user: UserEntity) {
    this.users.push(user);
  }

  @Mutation
  setSelectedUserRoles(roles: AuthRolesEnum[]) {
    this.selectedUser.roles = roles;
  }

  @Mutation
  setUsers(users: UserEntity[]) {
    this.users = users;
  }

  @Action
  async getSelectedUser(userId: string) {
    try {
      this.context.commit("setSelectedUser", deepCopy(this._emptyUserEntity));
      const user = await AdminUserService.get(userId);
      this.context.commit("setSelectedUser", user);
      return user;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: `Could not load User as selected ${userId}`
      });
    }
  }

  @Action
  async getUsers(ids?: string[]) {
    try {
      let users: UserEntity[] | ThgUserViewmodelGen[] | undefined;
      if (ids) {
        // must not request too many ids
        while (ids.length > 0) {
          const partialIds = ids.splice(0, 50);
          users = [
            ...((users as ThgUserViewmodelGen[]) || []),
            ...((await AdminSharedUserService.getAll({ ids: partialIds })) || [])
          ];
        }
      } else {
        users = await AdminUserService.getAll();
      }
      this.context.commit("setUsers", users);
      return users;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not load Users"
      });
    }
  }

  @Action
  async removePermission(updatePermission: IUserPermissionUpdateDto) {
    try {
      const updatedUser = await AdminUserService.removePermission(updatePermission);
      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not remove Permission from User."
      });
    }
  }

  @Action
  async addPermissionsByUser(user: UserEntity) {
    try {
      const updatedUser = await AdminUserService.addPermissionsByUser(user);
      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not add Permission to User."
      });
    }
  }

  @Action
  async addPermissions(data: { user: UserEntity; permission: MrfiktivPermissionDtoGen[] }) {
    try {
      const updatedUser = await AdminUserService.addPermissions(data.user._id, { permission: data.permission });

      this.context.commit("setSelectedUser", updatedUser);

      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not add Permission to User."
      });
    }
  }

  @Action
  async updateSelectedUserRoles(dto: IUserRoleUpdateDto) {
    try {
      this.context.commit("setSelectedUserRoles", dto.role);
      const updatedUser = await AdminUserService.updateRole(dto);
      return updatedUser;
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not update User Roles"
      });
    }
  }
  @Action
  async updateUser(userDto: { id: string; updateUserDto: MrfiktivUpdateUserDtoGen }) {
    try {
      const user = await AdminUserService.update(userDto.id, userDto.updateUserDto);
      this.context.commit("setSelectedUser", user);
    } catch (error) {
      Vue.$log.error(error);
      ErrorLogModule.addErrorLog({
        name: "User Error",
        message: "Could not update the User from UserAdministration Module."
      });
    }
  }

  @Action
  async invite(createUser: MrfiktivCreateUserDtoGen) {
    const createdUser = await AdminUserService.invite(createUser);
    this.context.commit("setSelectedUser", createdUser);
    this.context.commit("addUserToUsers", createdUser);
    return createdUser;
  }

  @Action
  async resendInvitation() {
    const user = await AdminUserService.resendInvitation(this.selectedUser._id);

    return user;
  }

  /**
   * Returns identity information from aws user pool
   * @param userMail
   * @returns
   */
  @Action
  async getIdentity(userMail: string): Promise<void> {
    const identityInfo = await authService.getIdentity(userMail);

    this.context.commit("_mutateSelectedUserIdentity", identityInfo);
  }

  @Action
  async delete(id: string) {
    if (this.selectedUser._id !== id) {
      Vue.$toast("ID Missmatch");
      return;
    }
    const user = await AdminUserService.delete(id);

    return user;
  }
}

export const UserAdministrationModule = getModule(UserAdministrationStore);
